import { FC, FormEvent, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import CaresPageHeader from '../../shell/cares-page-header';
import type { AdminSection } from '../config';

type AdminAuditActivityPageProps = {
  section: AdminSection;
  ownerEmail: string | undefined;
};

type AuditEventRecord = {
  id: number;
  event_key: string;
  scope: string;
  outcome: string;
  reason: string | null;
  occurred_at: string | null;
  metadata: Record<string, unknown>;
  actor: {
    id: number;
    name: string;
    email: string;
  } | null;
  target_user: {
    id: number;
    name: string;
    email: string;
  } | null;
  company: {
    id: number;
    name: string;
    slug: string;
  } | null;
  group: {
    id: number;
    name: string;
    slug: string;
  } | null;
};

type BreakGlassSessionRecord = {
  id: number;
  status: string;
  reason: string;
  started_at: string | null;
  expires_at: string | null;
  ended_at: string | null;
  ended_reason: string | null;
  user: {
    id: number;
    name: string;
    email: string;
    is_break_glass_local: boolean;
  } | null;
  company: {
    id: number;
    name: string;
    slug: string;
  } | null;
};

type AuditActivityResponse = {
  filters: {
    company_id: number | null;
    limit: number;
  };
  companies: Array<{
    id: number;
    name: string;
    slug: string;
    status: string;
  }>;
  break_glass: {
    eligible: boolean;
    authorization_notice: string;
    current_session: BreakGlassSessionRecord | null;
    active_sessions: BreakGlassSessionRecord[];
  };
  events: AuditEventRecord[];
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const formatDateTime = (value: string | null) => {
  if (!value) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const formatMetadataEntries = (metadata: Record<string, unknown>) =>
  Object.entries(metadata).filter(([, value]) => value !== null && value !== undefined && value !== '');

const humanizeEventKey = (value: string) =>
  value
    .split('.')
    .map((part) => part.replaceAll('_', ' '))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' / ');

const outcomeTone = (outcome: string) => {
  if (outcome === 'success') {
    return 'success';
  }

  if (outcome === 'denied') {
    return 'danger';
  }

  return 'warning';
};

const AdminAuditActivityPage: FC<AdminAuditActivityPageProps> = ({ section }) => {
  const [payload, setPayload] = useState<AuditActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [breakGlassReason, setBreakGlassReason] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [breakGlassCompanyId, setBreakGlassCompanyId] = useState('');
  const [endReason, setEndReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadActivity = async (companyId = selectedCompanyId) => {
    const params = new URLSearchParams();
    params.set('limit', '50');
    if (companyId !== '') {
      params.set('company_id', companyId);
    }

    const response = await fetch(`/api/v1/secure/admin/audit-activity?${params.toString()}`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Unable to load audit activity (${response.status}).`);
    }

    const nextPayload = (await response.json()) as AuditActivityResponse;
    setPayload(nextPayload);
    setSelectedCompanyId(nextPayload.filters.company_id ? String(nextPayload.filters.company_id) : '');
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        if (!cancelled) {
          await loadActivity(selectedCompanyId);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load audit activity.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  const handleActivateBreakGlass = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/v1/secure/admin/break-glass-sessions', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          reason: breakGlassReason.trim(),
          duration_minutes: Number(durationMinutes),
          company_id: breakGlassCompanyId === '' ? null : Number(breakGlassCompanyId),
        }),
      });

      const responsePayload = (await response.json()) as { message?: string; errors?: Record<string, string[]> };
      if (response.status === 422) {
        throw new Error(
          responsePayload.errors?.reason?.[0]
            ?? responsePayload.errors?.duration_minutes?.[0]
            ?? responsePayload.message
            ?? 'Unable to activate break-glass mode.',
        );
      }

      if (!response.ok) {
        throw new Error(responsePayload.message ?? `Unable to activate break-glass mode (${response.status}).`);
      }

      setBreakGlassReason('');
      setEndReason('');
      setSuccess('Break-glass mode is active and has been recorded in the audit trail.');
      await loadActivity(selectedCompanyId);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to activate break-glass mode.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEndBreakGlass = async () => {
    const sessionId = payload?.break_glass.current_session?.id;
    if (!sessionId) {
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`/api/v1/secure/admin/break-glass-sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          reason: endReason.trim() === '' ? null : endReason.trim(),
        }),
      });

      const responsePayload = (await response.json()) as { message?: string; errors?: Record<string, string[]> };
      if (!response.ok) {
        throw new Error(responsePayload.message ?? `Unable to end break-glass mode (${response.status}).`);
      }

      setEndReason('');
      setSuccess('Break-glass mode has been ended and the closure was recorded.');
      await loadActivity(selectedCompanyId);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to end break-glass mode.');
    } finally {
      setSubmitting(false);
    }
  };

  const currentSession = payload?.break_glass.current_session ?? null;
  const activeSessions = payload?.break_glass.active_sessions ?? [];
  const events = payload?.events ?? [];

  return (
    <>
      <CaresPageHeader homepage="CARES Administration" activepage="Admin" page={section.navLabel} />

      <div>
              {payload ? (
                <Alert variant="info" className="shadow-none">
                  <div className="fw-semibold mb-1">What break-glass means</div>
                  <div>
                    Break-glass is emergency, time-limited visibility for a designated local platform administrator
                    when normal support access is insufficient. Activation requires a reason, scope, and duration;
                    it is fully audited and expires automatically. It does not impersonate a user, activate client
                    data, or bypass tenant and department permissions.
                  </div>
                  <div className="fs-12 mt-2">{payload.break_glass.authorization_notice}</div>
                </Alert>
              ) : null}
              {error ? <Alert variant="danger" className="shadow-none">{error}</Alert> : null}
              {success ? <Alert variant="success" className="shadow-none">{success}</Alert> : null}

              <Row className="g-4 mb-4">
                <Col xl={4}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Emergency Access State</div>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column gap-3">
                      <div>
                        <div className="fs-12 text-uppercase text-muted mb-1">Eligibility</div>
                        <Badge bg="" className={payload?.break_glass.eligible ? 'bg-success-transparent text-success' : 'bg-warning-transparent text-warning'}>
                          {payload?.break_glass.eligible ? 'Eligible local account' : 'View only'}
                        </Badge>
                      </div>
                      <div>
                        <div className="fs-12 text-uppercase text-muted mb-1">Current session</div>
                        {currentSession ? (
                          <ListGroup variant="flush">
                            <ListGroup.Item className="px-0">
                              <div className="fw-semibold">{currentSession.company?.name ?? 'Platform-wide session'}</div>
                              <div className="text-muted fs-12">Started {formatDateTime(currentSession.started_at)}</div>
                              <div className="text-muted fs-12">Expires {formatDateTime(currentSession.expires_at)}</div>
                              <div className="mt-2">{currentSession.reason}</div>
                            </ListGroup.Item>
                          </ListGroup>
                        ) : (
                          <div className="text-muted">No active break-glass session for this account.</div>
                        )}
                      </div>
                      {currentSession ? (
                        <>
                          <Form.Group>
                            <Form.Label>End-session note</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={endReason}
                              onChange={(event) => setEndReason(event.target.value)}
                              placeholder="Optional closure note"
                            />
                          </Form.Group>
                          <Button variant="outline-danger" onClick={handleEndBreakGlass} disabled={submitting}>
                            {submitting ? 'Ending...' : 'End break-glass mode'}
                          </Button>
                        </>
                      ) : null}
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={4}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Activate Break-Glass</div>
                    </Card.Header>
                    <Card.Body>
                      {!payload?.break_glass.eligible ? (
                        <Alert variant="warning" className="shadow-none mb-0">
                          Only local platform-admin accounts marked for break-glass can activate this state.
                        </Alert>
                      ) : (
                        <Form className="d-flex flex-column gap-3" onSubmit={handleActivateBreakGlass}>
                          <Form.Group>
                            <Form.Label>Reason</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={breakGlassReason}
                              onChange={(event) => setBreakGlassReason(event.target.value)}
                              placeholder="Why is emergency access visibility needed?"
                              required
                            />
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>Tenant scope</Form.Label>
                            <Form.Select value={breakGlassCompanyId} onChange={(event) => setBreakGlassCompanyId(event.target.value)}>
                              <option value="">Platform-wide visibility only</option>
                              {(payload?.companies ?? []).map((company) => (
                                <option key={company.id} value={company.id}>
                                  {company.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                          <Form.Group>
                            <Form.Label>Duration</Form.Label>
                            <Form.Select value={durationMinutes} onChange={(event) => setDurationMinutes(event.target.value)}>
                              <option value="30">30 minutes</option>
                              <option value="60">60 minutes</option>
                              <option value="120">120 minutes</option>
                              <option value="240">240 minutes</option>
                            </Form.Select>
                          </Form.Group>
                          <Button type="submit" disabled={submitting || currentSession !== null}>
                            {submitting ? 'Activating...' : 'Activate break-glass mode'}
                          </Button>
                        </Form>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={4}>
                  <Card className="custom-card border shadow-none h-100 mb-0">
                    <Card.Header>
                      <div className="card-title">Active Sessions</div>
                    </Card.Header>
                    <Card.Body>
                      {activeSessions.length === 0 ? (
                        <div className="text-muted">No admins currently have an active break-glass session.</div>
                      ) : (
                        <ListGroup variant="flush">
                          {activeSessions.map((session) => (
                            <ListGroup.Item key={session.id} className="px-0">
                              <div className="d-flex justify-content-between gap-3">
                                <div>
                                  <div className="fw-semibold">{session.user?.name ?? session.user?.email ?? 'Unknown admin'}</div>
                                  <div className="text-muted fs-12">{session.company?.name ?? 'Platform-wide visibility'}</div>
                                </div>
                                <Badge bg="" className="bg-warning-transparent text-warning align-self-start">
                                  Active
                                </Badge>
                              </div>
                              <div className="text-muted fs-12 mt-1">Expires {formatDateTime(session.expires_at)}</div>
                              <div className="mt-2">{session.reason}</div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Card className="custom-card border shadow-none mb-0">
                <Card.Header className="d-flex flex-wrap justify-content-between gap-3">
                  <div className="card-title mb-0">Recent Audit Events</div>
                  <Form.Group className="mb-0" controlId="audit-activity-company-filter">
                    <Form.Label className="fs-12 text-uppercase text-muted mb-1">Tenant scope filter</Form.Label>
                    <Form.Select
                      aria-label="Filter audit events by tenant scope"
                      value={selectedCompanyId}
                      onChange={(event) => setSelectedCompanyId(event.target.value)}
                      style={{ maxWidth: '260px' }}
                    >
                      <option value="">All tenant scopes</option>
                      {(payload?.companies ?? []).map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Card.Header>
                <Card.Body>
                  {loading ? (
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <Spinner size="sm" animation="border" />
                      Loading audit activity...
                    </div>
                  ) : events.length === 0 ? (
                    <div className="text-muted">No audit events have been recorded for the current filter yet.</div>
                  ) : (
                    <>
                      <div className="d-md-none">
                        <div className="d-flex flex-column gap-3">
                          {events.map((record) => {
                            const metadataEntries = formatMetadataEntries(record.metadata);

                            return (
                              <Card key={record.id} className="border shadow-none mb-0">
                                <Card.Body className="d-flex flex-column gap-3">
                                  <div className="d-flex justify-content-between align-items-start gap-3">
                                    <div>
                                      <div className="fw-semibold">{humanizeEventKey(record.event_key)}</div>
                                      <div className="text-muted fs-12">{formatDateTime(record.occurred_at)}</div>
                                    </div>
                                    <Badge bg="" className={`bg-${outcomeTone(record.outcome)}-transparent text-${outcomeTone(record.outcome)}`}>
                                      {record.outcome}
                                    </Badge>
                                  </div>
                                  {record.reason ? <div className="text-muted fs-13">{record.reason}</div> : null}
                                  <div>
                                    <div className="text-uppercase text-muted fs-11 mb-1">Actor</div>
                                    <div>{record.actor?.name ?? 'System'}</div>
                                    <div className="text-muted fs-12 text-break">{record.actor?.email ?? 'No actor email'}</div>
                                  </div>
                                  <div>
                                    <div className="text-uppercase text-muted fs-11 mb-1">Scope</div>
                                    <div className="text-capitalize">{record.scope}</div>
                                    <div className="text-muted fs-12 text-break">
                                      {record.company?.name ?? 'No tenant scope'}
                                      {record.group?.name ? ` · Department: ${record.group.name}` : ''}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-uppercase text-muted fs-11 mb-1">Target</div>
                                    <div>{record.target_user?.name ?? 'No user target'}</div>
                                    <div className="text-muted fs-12 text-break">{record.target_user?.email ?? 'No target email'}</div>
                                  </div>
                                  {metadataEntries.length > 0 ? (
                                    <div>
                                      <div className="text-uppercase text-muted fs-11 mb-1">Metadata</div>
                                      <ListGroup variant="flush">
                                        {metadataEntries.map(([key, value]) => (
                                          <ListGroup.Item key={key} className="px-0 py-2 border-0">
                                            <div className="text-uppercase text-muted fs-11 mb-1">{key.replaceAll('_', ' ')}</div>
                                            <div className="fs-12 text-break">{String(value)}</div>
                                          </ListGroup.Item>
                                        ))}
                                      </ListGroup>
                                    </div>
                                  ) : null}
                                </Card.Body>
                              </Card>
                            );
                          })}
                        </div>
                      </div>

                      <div className="d-none d-md-block">
                        <Table responsive className="table align-middle mb-0">
                          <thead>
                            <tr>
                              <th>When</th>
                              <th>Action</th>
                              <th>Actor</th>
                              <th>Scope</th>
                              <th>Target</th>
                              <th>Outcome</th>
                            </tr>
                          </thead>
                          <tbody>
                            {events.map((record) => {
                              const metadataEntries = formatMetadataEntries(record.metadata);

                              return (
                                <tr key={record.id}>
                                  <td>
                                    <div>{formatDateTime(record.occurred_at)}</div>
                                    {record.reason ? <div className="text-muted fs-12 text-break">{record.reason}</div> : null}
                                  </td>
                                  <td>
                                    <div className="fw-semibold text-break">{humanizeEventKey(record.event_key)}</div>
                                    {record.group?.name ? <div className="text-muted fs-12 text-break">Department: {record.group.name}</div> : null}
                                    {metadataEntries.length > 0 ? (
                                      <div className="text-muted fs-12 text-break mt-1">
                                        {metadataEntries.map(([key, value]) => `${key.replaceAll('_', ' ')}: ${String(value)}`).join(' · ')}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td>
                                    <div>{record.actor?.name ?? 'System'}</div>
                                    <div className="text-muted fs-12 text-break">{record.actor?.email ?? 'No actor email'}</div>
                                  </td>
                                  <td>
                                    <div className="text-capitalize">{record.scope}</div>
                                    <div className="text-muted fs-12 text-break">{record.company?.name ?? 'No tenant scope'}</div>
                                  </td>
                                  <td>
                                    <div>{record.target_user?.name ?? 'No user target'}</div>
                                    <div className="text-muted fs-12 text-break">{record.target_user?.email ?? 'No target email'}</div>
                                  </td>
                                  <td>
                                    <Badge bg="" className={`bg-${outcomeTone(record.outcome)}-transparent text-${outcomeTone(record.outcome)}`}>
                                      {record.outcome}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
      </div>
    </>
  );
};

export default AdminAuditActivityPage;
